<template>
    <dialog
        popover="manual"
        ref="tile"
        class="mtd-tile"
        :style="{ width: `${state.width}px`, height: `${state.height}px` }"
    >
        <header
            ref="header"
            class="mtd-header"
            @dragstart.prevent
            @contextmenu.stop="openContextMenu"
        >
            <div class="mtd-title">
                <mg-icon
                    v-if="state.icon"
                    :icon="state.icon"
                    class="mtd-icon"
                />

                <Mfm v-if="state.title" :text="state.title" />
            </div>
            <div class="mtd-controls">
                <button
                    class="_button mtd-deco-control mtd-x"
                    @click.prevent="tileControls.setClosing()"
                >
                    <mg-icon icon="x" size="xl" />
                </button>
            </div>
        </header>
        <div class="mtd-content">
            <slot :controls="tileControls" />
        </div>
    </dialog>
</template>

<script setup lang="ts">
import {
    computed,
    onMounted,
    onWatcherCleanup,
    useTemplateRef,
    watchEffect,
} from "vue";
import { MenuItem } from "@/components/context-menu/mag-dynamic-menu";
import { useFluent } from "fluent-vue";
import {
    ContextMenuDynamic,
    magContextMenu,
} from "@/components/context-menu/mag-context-menu";
import { copyToClipboard } from "@/scripts/browser-util";
import { mainRouter } from "@/router";
import { TileControls, TileState, useTileStore } from "@/store/tile-store";
import { useDraggable, useEventListener } from "@vueuse/core";

const tile = useTemplateRef("tile");
const header = useTemplateRef("header");
const { $t } = useFluent();

const tileStore = useTileStore();

const state = defineModel<TileState<object>>("state", {
    required: true,
});

const tileControls = {
    setTitle(title: string) {
        state.value.title = title;
    },
    setIcon(icon?: string) {
        state.value.icon = icon;
    },
    setClosing() {
        state.value.closing = true;
    },
    bringToForeground() {
        tileStore.bringToForeground(state.value.id);
    },
} satisfies TileControls;

const { position } = useDraggable(tile, {
    handle: header,
});

useEventListener(
    header,
    "mousedown",
    (e) => {
        tileControls.bringToForeground();
    },
    {
        passive: true,
    },
);

watchEffect(() => {
    const pos = position.value;
    const tileEl = tile.value;

    if (!tileEl) {
        return;
    }

    const frame = requestAnimationFrame(() => {
        tileEl.style.translate = `${pos.x}px ${pos.y}px`;
    });

    onWatcherCleanup(() => {
        cancelAnimationFrame(frame);
    });
});

onMounted(() => {
    tile.value?.showPopover();
});

function expand() {
    mainRouter.push({
        ...mainRouter.resolve(router.currentRoute.value),
        query: {
            inPage: true.toString(),
        },
    });
    windowEl.value?.close();
}

function popout() {
    _popout(router.currentRoute.value.fullPath, windowEl.value?.$el);
    windowEl.value?.close();
}

const contextmenu = computed<MenuItem[]>(() => [
    {
        type: "button",
        props: {
            icon: "arrows-out-simple",
        },
        text: $t("showInPage"),
        action: expand,
    },
    {
        type: "button",
        props: {
            icon: "arrow-square-out",
        },
        text: $t("popout"),
        action: popout,
    },
    {
        type: "button",
        props: {
            icon: "arrow-square-out",
        },
        text: $t("openInNewTab"),
        action: () => {
            window.open(url + router.currentRoute.value.fullPath, "_blank");
            emit("close");
        },
    },
    {
        type: "button",
        props: {
            icon: "link-simple",
        },
        text: $t("copyLink"),
        action: () => {
            copyToClipboard(url + router.currentRoute.value.fullPath);
        },
    },
]);

function openContextMenu(ev: MouseEvent) {
    magContextMenu(
        {
            items: contextmenu.value satisfies MenuItem[],
        },
        ContextMenuDynamic,
        {
            parent: (ev.currentTarget ?? ev.target) as
                | HTMLElement
                | null
                | undefined,
        },
    );
}

defineSlots<{
    default: ({ controls }: { controls: TileControls }) => any;
}>();

const emit = defineEmits<{
    close: [];
}>();
</script>

<style scoped lang="scss">
.mtd-tile {
    --safe-margin: 8px;
    --header-height: 48px;

    padding: 0;
    border-radius: var(--radius);
    border: 1px solid var(--accentedBg);
    max-height: calc(100vh - 2 * var(--safe-margin));
    max-width: calc(100vw - 2 * var(--safe-margin));
    overflow: clip;
    display: flex;
    flex-direction: column;
    box-shadow:
        0 4px 4px var(--shadow),
        0 6px 8px var(--shadow);
    will-change: translate;
    inset: 0 auto auto 0;

    .mtd-header {
        display: flex;
        height: var(--header-height);
        background-color: var(--windowHeader);
        border-bottom: 1px solid var(--accentedBg);
        align-items: center;
        user-select: none;

        .mtd-title {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            flex-grow: 1;
            flex-shrink: 1;
            padding: 0 12px;
            font-size: 1.1rem;
            color: var(--navFg);
            font-weight: 500;
            height: 100%;
            align-items: center;
            display: flex;

            .mtd-icon {
                margin-right: 8px;
            }
        }

        .mtd-controls {
            height: 100%;
            align-items: center;
            display: flex;
            gap: 4px;
            padding: 0 8px;

            .mtd-deco-control {
                aspect-ratio: 1;
                border: 1px solid var(--accentedBg);
                border-radius: calc(var(--radius) / 2);
                padding: 4px;

                mg-icon {
                    display: block;
                    height: 100%;
                }

                &:hover {
                    background-color: var(--buttonHoverBg);
                    color: var(--accent);
                }
            }
        }
    }

    .mtd-content {
        flex-grow: 1;
        contain: strict;
        overflow: auto;
    }
}
</style>
